/*
  Infinite scroll for product grids, in place of numbered pagination.

  When the element nears the viewport the next page is fetched through the
  Section Rendering API and its items are appended to the grid, until there
  is no next page.

  The list to append to is always the element immediately before
  <koko-infinite-scroll>, both on the live page and in the fetched HTML. That
  covers the collection grid (ul#product-grid) and the search grid
  (#product-grid > ul) without a selector per template.

  The fallback link is a real href to the next page, so crawlers and no-JS
  visitors still reach every product. It is hidden while scrolling works and
  shown again if a fetch fails, so the shopper can carry on by hand.

  Filtering and sorting replace #ProductGridContainer wholesale, which
  disconnects this element and connects a fresh one with the right next URL.
*/
if (!customElements.get('koko-infinite-scroll')) {
  class KokoInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl;
      this.list = this.previousElementSibling;
      this.link = this.querySelector('.koko-infinite__link');
      this.status = this.querySelector('.koko-infinite__status');
      if (!this.nextUrl || !this.list || !('IntersectionObserver' in window)) return;

      this.classList.add('is-enhanced');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: '0px 0px 800px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');
      this.controller = new AbortController();

      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('koko-infinite-scroll');
        const incomingList = incoming?.previousElementSibling;
        if (!incomingList) throw new Error('Product grid not found in response');

        // Register the reveal animation while the items are still staged, so
        // only the new cards are observed; the cards already on the page are
        // left alone and don't re-animate when scrolled back to.
        const staging = document.createElement('div');
        staging.append(...incomingList.children);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(staging);
        const added = staging.children.length;
        this.list.append(...staging.children);

        this.nextUrl = incoming.dataset.nextUrl;
        if (this.status) this.status.textContent = this.dataset.loadedMessage.replace('[count]', added);

        if (!this.nextUrl) {
          this.finish();
          return;
        }
        if (this.link) {
          const clean = new URL(this.nextUrl, window.location.origin);
          clean.searchParams.delete('section_id');
          this.link.href = clean.pathname + clean.search;
        }
        // An intersection callback only fires on change. If the sentinel is
        // still within range after appending (a short page), re-observing
        // triggers a fresh check so loading continues.
        this.observer.unobserve(this);
        this.observer.observe(this);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Infinite scroll:', error);
        this.observer.disconnect();
        this.classList.remove('is-enhanced');
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }
    }

    finish() {
      this.observer.disconnect();
      this.hidden = true;
    }
  }

  customElements.define('koko-infinite-scroll', KokoInfiniteScroll);
}
